/**
 * Supabase Client - Production-safe, universal (server + browser).
 *
 * Env var resolution priority (both server and browser supported):
 *   URL:  SUPABASE_URL → VITE_SUPABASE_URL → runtime config → localhost fallback
 *   KEY:  SUPABASE_ANON_KEY → VITE_SUPABASE_PUBLISHABLE_KEY → runtime config → placeholder
 *
 * Server-side code (Node/Express) sets SUPABASE_URL and SUPABASE_ANON_KEY.
 * Browser code (Vite) exposes VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY
 * baked at build time. When those are absent, the browser client self-heals by
 * fetching /api/runtime-config (proxied to the Railway server that has the real
 * env vars) and calling refreshSupabaseClient() with the fetched values.
 *
 * For service-role access in server code, use createServiceClient() which
 * reads SUPABASE_SERVICE_ROLE_KEY and disables session persistence.
 */

import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";

/* =========================================================
   ENV RESOLUTION
   Priority: Node process.env → Vite build constants → fallback.
   Never uses import.meta (breaks CommonJS build).

   __SUPABASE_URL__ / __SUPABASE_KEY__ are injected by vite.config.ts
   `define` at browser build time. In Node.js server context they are not
   defined; `typeof` is safe for undeclared identifiers in JavaScript.
   ========================================================= */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const __SUPABASE_URL__: string | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const __SUPABASE_KEY__: string | undefined;

function resolveEnv(primary: string, fallback: string): string {
  if (typeof process !== "undefined") {
    if (process.env?.[primary])  return process.env[primary] as string;
    if (process.env?.[fallback]) return process.env[fallback] as string;
  }
  return "";
}

let SUPABASE_URL =
  resolveEnv("SUPABASE_URL", "VITE_SUPABASE_URL") ||
  (typeof __SUPABASE_URL__ !== "undefined" ? __SUPABASE_URL__ : "");

let SUPABASE_ANON_KEY =
  resolveEnv("SUPABASE_ANON_KEY", "VITE_SUPABASE_PUBLISHABLE_KEY") ||
  (typeof __SUPABASE_KEY__ !== "undefined" ? __SUPABASE_KEY__ : "");

/* =========================================================
   RUNTIME DETECTION
   ========================================================= */

const isBrowser =
  typeof window !== "undefined" &&
  typeof window.localStorage !== "undefined";

const safeStorage = isBrowser ? window.localStorage : undefined;

/* =========================================================
   VALIDATION
   ========================================================= */

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn(
    "[Supabase] Missing env vars. Server expects SUPABASE_URL + SUPABASE_ANON_KEY; " +
    "browser expects VITE_SUPABASE_URL + VITE_SUPABASE_PUBLISHABLE_KEY. " +
    "The browser client will self-heal via /api/runtime-config."
  );
}

/* =========================================================
   CLIENT OPTIONS
   ========================================================= */

function makeClientOptions() {
  return {
    auth: {
      storage:            safeStorage,
      persistSession:     isBrowser,
      autoRefreshToken:   isBrowser,
      detectSessionInUrl: isBrowser,

      // PKCE, explicitly, because the default is NOT what this product needs and the mismatch was
      // silent. supabase-js defaults flowType to "implicit", which returns the session as a URL
      // FRAGMENT. Nuvra's native sign-in return handler (src/shell/nativeShell.ts) calls
      // exchangeCodeForSession first, which is the PKCE path: the handler was written for one flow
      // while the client was configured for the other, so its primary branch could never fire.
      //
      // PKCE is also simply the correct flow for a public client. It returns a CODE in the query
      // string rather than tokens in a fragment, which matters on Android: a custom-scheme intent
      // carries a query reliably, and the access token is never placed in a URL that the OS, the
      // browser and any handler in between can all see.
      flowType:           "pkce" as const,
    },
    global: {
      headers: { "x-client-info": "nuvra-production" },
    },
  };
}

/* =========================================================
   ANON / PUBLIC CLIENT - Proxy-backed so it can be refreshed
   at runtime without changing any import site.

   The Proxy always delegates to the current _client. When
   refreshSupabaseClient() is called (after fetching runtime
   config), _client is swapped and all subsequent calls
   automatically use the fresh client - including realtime
   subscriptions re-subscribed by their hooks.
   ========================================================= */

// Internal mutable reference to the active client
let _client = createClient<Database>(
  SUPABASE_URL     || "http://localhost:54321",
  SUPABASE_ANON_KEY || "public-anon-key",
  makeClientOptions(),
);

// The exported singleton - a Proxy that always reads from _client.
// Because the get trap closes over the variable NAME _client (not its value),
// any reassignment of _client is immediately visible through the Proxy.
export const supabase = new Proxy(_client, {
  get(_target, prop, receiver) {
    return Reflect.get(_client, prop, receiver);
  },
}) as typeof _client;

/**
 * Swap the active Supabase client (browser only).
 * Call this after fetching real config from /api/runtime-config.
 * All subsequent supabase.from() / supabase.auth calls use the new client.
 * Existing realtime subscriptions need to be re-subscribed by their hooks
 * (which will happen naturally on the next React effect re-run).
 */
export function refreshSupabaseClient(url: string, anonKey: string): void {
  if (!url || !anonKey) return;
  // Same target: keep the existing client. Creating a second GoTrueClient for
  // identical credentials only multiplies auth-lock contention.
  if (url === SUPABASE_URL && anonKey === SUPABASE_ANON_KEY) return;
  const previous = _client;
  SUPABASE_URL = url;
  SUPABASE_ANON_KEY = anonKey;
  _client = createClient<Database>(url, anonKey, makeClientOptions());
  // The abandoned instance MUST stop its background token auto-refresh loop.
  // Both instances share the navigator.locks mutex "lock:sb-<ref>-auth-token";
  // a still-running old loop steals the lock from the live client and every
  // getSession() in the app can then fail with "Lock ... was released because
  // another request stole it", surfacing as false 401 "Sign in again" states.
  try { previous.auth.stopAutoRefresh(); } catch { /* previous client is already unusable */ }
}

/* =========================================================
   RUNTIME CONFIG SELF-HEAL
   Fetches /api/runtime-config once when the build-time baked
   Supabase URL is absent or a localhost fallback. The endpoint
   is served by the Railway API server (which always has the real
   env vars) and proxied through Vercel's /api/* rewrite rule.
   ========================================================= */

let _configFetched  = false;
let _configFetching = false;
const _configWaiters: Array<() => void> = [];

/**
 * Ensure the browser Supabase client is pointed at the real database.
 * Safe to call multiple times - the fetch runs only once. Returns true
 * when the client is using a non-localhost URL.
 */
export async function ensureSupabaseConfig(): Promise<boolean> {
  // Already configured with a real URL
  const urlOk = SUPABASE_URL && !SUPABASE_URL.includes("localhost") && !SUPABASE_URL.includes("127.0.0.1");
  if (urlOk) { _configFetched = true; return true; }

  // Already fetched (or in progress) - wait for it
  if (_configFetching) {
    return new Promise<boolean>(resolve => {
      _configWaiters.push(() => resolve(_configFetched));
    });
  }

  _configFetching = true;

  try {
    const res = await fetch("/api/runtime-config", {
      method: "GET",
      headers: { "x-client-info": "nuvra-runtime-init" },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const cfg = await res.json() as { supabaseUrl?: string; supabaseAnonKey?: string };

    if (
      cfg.supabaseUrl &&
      !cfg.supabaseUrl.includes("localhost") &&
      !cfg.supabaseUrl.includes("127.0.0.1") &&
      cfg.supabaseAnonKey
    ) {
      refreshSupabaseClient(cfg.supabaseUrl, cfg.supabaseAnonKey);
      _configFetched = true;
      console.info("[Supabase] Client refreshed via /api/runtime-config");
    }
  } catch (err) {
    console.warn("[Supabase] /api/runtime-config fetch failed:", (err as Error).message);
  }

  _configFetching = false;
  _configWaiters.forEach(cb => cb());
  _configWaiters.length = 0;
  return _configFetched;
}

/* =========================================================
   SERVICE CLIENT FACTORY
   Returns a service-role client that bypasses RLS.
   Only usable server-side (requires SUPABASE_SERVICE_ROLE_KEY).
   ========================================================= */

/**
 * The one live service client, and the credentials it was built for.
 *
 * WHY THIS EXISTS. The factory used to construct a NEW SupabaseClient on every call, and its own
 * comment told callers to "cache the result at module scope, not call this per-request". Nothing
 * could: most of the 900+ call sites are inside per-request functions, so the advice was
 * unfollowable and universally unfollowed. Measured: 69us at p50 and 783us at p99 per construction,
 * with about 11 KB of heap each, against 0.1us to reuse one. One operations role run alone built
 * eleven of them, one per query it issued.
 *
 * WHY IT IS SAFE. Every construction produced a functionally identical client: same URL, same
 * service key, same options, `persistSession: false` and `autoRefreshToken: false` so there is no
 * session state to leak between callers. No call site mutates the returned client's auth state or
 * opens realtime channels on it (the `signInWith*` calls in the auth service are on the ANON client,
 * a different instance, and `auth.admin.*` is a stateless REST surface). Reuse therefore changes
 * nothing a caller can observe.
 *
 * WHY IT IS KEYED. `SUPABASE_URL` is reassigned at runtime by `refreshSupabaseClient`, and the
 * service key is read from the environment on every call. Keying the memo on both means an env or
 * URL change still yields a correctly-configured client, exactly as before. This is a single slot,
 * never a map: a changed key replaces the previous client rather than accumulating one per value, so
 * the memory cost is one client, bounded and constant.
 *
 * WHAT IT IS NOT. Not a connection pool, not a cache of DATA, and not a cache of any authorization
 * decision. Every query, every RLS evaluation and every ownership check still executes exactly as it
 * did; only the object that carries them is reused.
 */
let _serviceClient: ReturnType<typeof createClient<Database>> | null = null;
let _serviceClientKey = "";

export function createServiceClient() {
  const serviceKey = typeof process !== "undefined"
    ? process.env?.SUPABASE_SERVICE_ROLE_KEY ?? ""
    : "";

  // Unchanged, and deliberately BEFORE the memo: a missing key throws on every call, so a
  // misconfigured server cannot be handed a client that an earlier correctly-configured call built.
  if (!serviceKey) {
    throw new Error(
      "[Supabase] createServiceClient() requires SUPABASE_SERVICE_ROLE_KEY. " +
      "This client must only be used in server-side code."
    );
  }

  const url = SUPABASE_URL || "http://localhost:54321";
  const identity = `${url}\u0000${serviceKey}`;
  if (_serviceClient && _serviceClientKey === identity) return _serviceClient;

  _serviceClient = createClient<Database>(
    url,
    serviceKey,
    { auth: { autoRefreshToken: false, persistSession: false } }
  );
  _serviceClientKey = identity;
  return _serviceClient;
}

/* =========================================================
   HEALTH CHECK
   ========================================================= */

export function isSupabaseConfigured(): boolean {
  return Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);
}

/** The resolved Supabase URL the browser client is currently using.
 *  Updates after refreshSupabaseClient() is called. */
export function getSupabaseUrl(): string {
  return SUPABASE_URL;
}

/** The resolved anon (publishable) key the browser client is using.
 *  Safe to expose: it is the same key already shipped to every browser.
 *  Needed for direct GoTrue endpoints like /auth/v1/settings. */
export function getSupabaseAnonKey(): string {
  return SUPABASE_ANON_KEY;
}
